import { useMemo } from "react"
import { add } from "dinero.js"
import { makeStyles } from "tss-react/mui"
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { AccountingDetailsReference } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { LocalizedEnum, LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { toCurrency, toDinero, zeroDinero } from "../../../../../modules/currency/currencyLib"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { useMoney } from "../../../../../modules/currency/useMoney"

const useStyles = makeStyles()((theme) => ({
    tableContainer: {
        background: theme.palette.tailwind.slate[50],
        [theme.breakpoints.down("sm")]: {
            order: 2,
            marginTop: "4rem",
        },
    },
    tableHeader: {
        fontSize: "1.063rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
        whiteSpace: "nowrap",
        lineHeight: "1.66",
    },
    tableCell: {
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        fontWeight: 400,
        [theme.breakpoints.down("sm")]: {
            padding: "1rem 0.5rem",
        },
    },
    idCell: {
        [theme.breakpoints.down("sm")]: {
            width: "3.625rem",
        },
    },
    totalAmount: {
        borderTop: `2px solid #000`,
    },
}))

type Props = Readonly<{
    references: AccountingDetailsReference[]
}>

export function CreditReferencesTable({ references }: Props) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})

    const totalDinero = useMemo(() => {
        if (references.length === 0) {
            return toDinero({ amount: 0, currency: "DKK", scale: 2 })
        }
        const monies = references.map((r) => toDinero(r.amount))

        const zero = zeroDinero(toCurrency("DKK"))

        return monies.reduce((acc, m) => add(acc, m), zero)
    }, [references])

    const totalFormatted = useMemo(() => moneyFactory.formatAsNumber(totalDinero), [moneyFactory, totalDinero])

    if (!references.length) return null

    return (
        <TableContainer className={classes.tableContainer}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <LocalizedStrict id="production-credit-note-table-column-order-id">
                            <TableCell className={classes.tableHeader}>Order ID</TableCell>
                        </LocalizedStrict>
                        <LocalizedStrict id="production-credit-note-table-column-type">
                            <TableCell className={classes.tableHeader}>Type</TableCell>
                        </LocalizedStrict>
                        <LocalizedStrict id="production-credit-note-table-column-booked">
                            <TableCell className={classes.tableHeader}>Booked</TableCell>
                        </LocalizedStrict>
                        <LocalizedStrict id="production-credit-note-table-column-amount">
                            <TableCell className={classes.tableHeader} align="right">
                                Amount (DKK)
                            </TableCell>
                        </LocalizedStrict>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {references.map((orderLine) => (
                        <TableRow key={orderLine.orderId}>
                            <TableCell className={cx([classes.tableCell, classes.idCell])} width="15%">
                                <Typography variant="body2">{orderLine.orderId}</Typography>
                            </TableCell>
                            <LocalizedEnum
                                base="production-credit-note-table-column-type-cell"
                                enumValue={orderLine.referenceType}
                            >
                                <TableCell className={classes.tableCell}>
                                    <Typography variant="body2">{orderLine.referenceType}</Typography>
                                </TableCell>
                            </LocalizedEnum>
                            <TableCell className={cx([classes.tableCell])} width="20%">
                                <Typography variant="body2">
                                    {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(orderLine.bookedTime))}
                                </Typography>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right" width="10%">
                                <Typography variant="body2">
                                    {moneyFactory.formatAsNumber(toDinero(orderLine.amount))}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell />
                        <TableCell />
                        <TableCell />
                        <TableCell align="right" className={classes.totalAmount}>
                            {totalFormatted}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    )
}
